import { ApiError } from '@/api/errors';
import { v2ErrorCode } from '@/api/v2Errors';

/**
 * DNKADM-0467: whether a failed order request may still have landed on the server.
 *
 * The three member money dialogs (sell cart, buy membership, walk-in sale) keep ONE
 * idempotency key per attempt. Dropping that key after a failure the server may already
 * have committed lets the next submit go out under a NEW key, and the server then grants
 * (or charges) a second time. So the key is dropped only when the server definitively
 * refused the request; every other failure is an UNKNOWN outcome and keeps the key.
 *
 * Unknown (keep the key, lock the cart, offer "Check again"):
 *  - anything that is not an `ApiError` — the request left the browser and something
 *    after it threw (an unreadable 2xx, a payment-action driver, a follow-up read);
 *  - `NETWORK_ERROR` / status `0` — the HTTP layer maps a transport failure, a timeout
 *    and an aborted request all onto this, with no response at all;
 *  - any status `>= 500`, with or without an envelope;
 *  - an envelope that says `retryable: true` (e.g. the request is still in progress);
 *  - any other `ApiError` WITHOUT a server envelope `code` (a bare 4xx from a proxy or
 *    gateway is not the order service refusing the order).
 *
 * Definitive (drop the key): a 4xx carrying a server envelope `code` — a PIN refusal,
 * a validation error, a decline.
 */
export function outcomeIsUnknown(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.code === 'NETWORK_ERROR' || error.status === 0) return true;
  if (error.status >= 500) return true;
  if (error.retryable === true) return true;
  return v2ErrorCode(error) === undefined;
}

/**
 * `booking` (DNKADM-0477, F-216): a book-on-behalf that takes no tender — paid with
 * credits, or Pay later — where what may have landed is the booking (and its credits).
 *
 * DNKADM-0479: `credits` (F-244) is a credit grant, reduction or expiry move, and `member`
 * (ruling 2) an Add member.
 */
export type UnconfirmedPaymentMode =
  | 'comp'
  | 'external'
  | 'card'
  | 'booking'
  | 'credits'
  | 'member';

/**
 * The copy for an unknown outcome, per payment path. "Check again" re-sends the SAME
 * request under the SAME key: the server answers a key it has seen with the order that
 * key already produced, so nothing is granted, recorded or charged twice.
 */
export function unconfirmedMessage(mode: UnconfirmedPaymentMode): string {
  if (mode === 'comp') return "We couldn't confirm the comp. Check again before trying anything else.";
  if (mode === 'booking') {
    return "We couldn't confirm the booking. Check again re-sends the same booking; it is booked, and any credits used, at most once.";
  }
  if (mode === 'credits') {
    return "We couldn't confirm the credit change. Check again re-sends the same change; it is applied at most once.";
  }
  if (mode === 'member') {
    return "We couldn't confirm the member was added. Check again re-sends the same request; the member is added, and the payment recorded, at most once.";
  }
  if (mode === 'external') {
    return "We couldn't confirm the sale was recorded. Check again re-sends the same request and will not record the payment twice.";
  }
  return "We couldn't confirm the payment. Check again re-sends the same payment; the card is charged at most once.";
}

/** DNKADM-0467: asked before a dialog with an unconfirmed attempt is closed. */
export const UNCONFIRMED_CLOSE_PROMPT =
  "We couldn't confirm the last sale. If you close now and sell again, it may be granted or charged twice. Close anyway?";

/**
 * DNKADM-0475 (0467 follow-up c): the same ask for the two booking dialogs that take money
 * (book with a card or external tender, and take payment on an unpaid booking).
 */
export const UNCONFIRMED_PAYMENT_CLOSE_PROMPT =
  "We couldn't confirm the last payment. If you close now and try again, it may be taken twice. Close anyway?";

/** DNKADM-0477 (F-216): the same ask for a credits or Pay later booking that took no tender. */
export const UNCONFIRMED_BOOKING_CLOSE_PROMPT =
  "We couldn't confirm the last booking. If you close now and book again, it may be booked twice. Close anyway?";

/** DNKADM-0479 (F-244): the same ask for a credit grant, reduction or expiry move. */
export const UNCONFIRMED_CREDITS_CLOSE_PROMPT =
  "We couldn't confirm the last credit change. If you close now and change the credits again, it may be applied twice. Close anyway?";

/** DNKADM-0479 (ruling 2): the same ask for an Add member. */
export const UNCONFIRMED_MEMBER_CLOSE_PROMPT =
  "We couldn't confirm the member was added. If you close now and add them again, they may be added twice. Close anyway?";

/**
 * DNKADM-0475 (F-100): asked before closing a dialog locked on a `NEEDS_REVIEW` order. The
 * outcome is known there: the card was charged and nothing was applied, so "couldn't
 * confirm" would be wrong.
 */
export const NEEDS_REVIEW_CLOSE_PROMPT =
  'The card was charged but nothing was applied yet. Check Payments before charging again. Close anyway?';

/** The close prompt for a locked attempt: the `NEEDS_REVIEW` copy, else the 0467 copy. */
export function unconfirmedClosePrompt(needsReview: boolean | undefined): string {
  return needsReview === true ? NEEDS_REVIEW_CLOSE_PROMPT : UNCONFIRMED_CLOSE_PROMPT;
}

/** DNKADM-0467: shown when "Check again" could not read the order. */
export const CHECK_AGAIN_FAILED = "Still couldn't confirm. Try again in a moment.";
